/**
 * 渠道房型价格：第三方接口直调 + 时间戳 + 本地缓存（内存 + localStorage）+ 字段对齐组件
 */

import {
  getChannelPriceFromThirdParty,
  getStoreRevenueComponent,
} from '../../../../../service/storeRevenueApi';
import type { PriceChannelResponse } from '../../../utils/priceChannelDataTransformer';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from '../../../utils/storeRevenueLocalStorage';

const CHANNEL_PRICE_TTL_MS = 2 * 60 * 1000; // 2 分钟

interface ChannelPriceEntry {
  data: PriceChannelResponse | null;
  fetchedAt: number;
}

const channelPriceCache = new Map<string, ChannelPriceEntry>();
const inFlightChannelPrice = new Map<string, Promise<PriceChannelResponse | null>>();

function cacheKey(hotelId: number | string): string {
  return `channelPrice:${String(hotelId)}`;
}

function isValid(entry: ChannelPriceEntry): boolean {
  return Date.now() - entry.fetchedAt < CHANNEL_PRICE_TTL_MS;
}

function isPriceChannelResponse(v: unknown): v is PriceChannelResponse {
  if (v == null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return o.code === 1 && o.data != null && typeof o.data === 'object' && Array.isArray((o.data as Record<string, unknown>).roomTypeList);
}

/** 冷启动从 localStorage 恢复，供 hook 做初始渲染 */
function hydrateFromLocalStorage(hotelId: number | string): PriceChannelResponse | null {
  const stored = getStoreFromLocalStorage(hotelId)?.channelPrice;
  if (!stored?.data || typeof stored.fetchedAt !== 'number') return null;
  if (Date.now() - stored.fetchedAt > CHANNEL_PRICE_TTL_MS) return null;
  if (!isPriceChannelResponse(stored.data)) return null;
  const entry: ChannelPriceEntry = { data: stored.data, fetchedAt: stored.fetchedAt };
  channelPriceCache.set(cacheKey(hotelId), entry);
  return stored.data;
}

export function getChannelPriceFromLocalStorageSync(hotelId: number | string): PriceChannelResponse | null {
  return hydrateFromLocalStorage(hotelId);
}

export async function getChannelPriceWithCache(hotelId: number | string): Promise<PriceChannelResponse | null> {
  const key = cacheKey(hotelId);
  let entry = channelPriceCache.get(key);
  if (entry && isValid(entry)) return entry.data;
  const fromLocal = hydrateFromLocalStorage(hotelId);
  if (fromLocal != null) return fromLocal;

  const inFlight = inFlightChannelPrice.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<PriceChannelResponse | null> => {
    try {
      const data = await getChannelPriceFromThirdParty(hotelId);
      const fetchedAt = Date.now();
      channelPriceCache.set(key, { data, fetchedAt });
      setStoreSliceInLocalStorage(hotelId, 'channelPrice', { data, fetchedAt });
      return data;
    } catch (directErr) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[渠道房型价格] 直调 PMS 失败，尝试后端回退', { hotelId, err: directErr });
      }
      try {
        const slice = await getStoreRevenueComponent(hotelId, 'channel-price');
        const chartData = (slice as { channelPrice?: { chartData?: unknown } }).channelPrice?.chartData;
        if (isPriceChannelResponse(chartData)) {
          const fetchedAt = Date.now();
          channelPriceCache.set(key, { data: chartData, fetchedAt });
          setStoreSliceInLocalStorage(hotelId, 'channelPrice', { data: chartData, fetchedAt });
          return chartData;
        }
      } catch {
        // ignore
      }
      return null;
    }
  })().finally(() => { inFlightChannelPrice.delete(key); });
  inFlightChannelPrice.set(key, promise);
  return promise;
}

export function clearChannelPriceCache(hotelId: number | string): void {
  const key = cacheKey(hotelId);
  channelPriceCache.delete(key);
  inFlightChannelPrice.delete(key);
}

export function __getChannelPriceCacheForDev(): Map<string, ChannelPriceEntry> {
  return channelPriceCache;
}
